// Floating feedback widget. Always-visible button bottom-right; clicking
// opens a modal with three structured fields plus a free-form box.
//
// The structured prompts target the two research questions we care about:
//   1. How would you like to interact with the service?
//   2. How could it reduce friction in your family setup?
// The free-form field at the bottom catches anything else.

import { useEffect, useState } from "react";
import { api } from "../lib/api.js";

const COOKIE_NAME = "inkling_lp_variant";

function readVariantCookie(): "A" | "B" | "C" | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|; )inkling_lp_variant=([ABC])/);
  return (m?.[1] as "A" | "B" | "C" | undefined) ?? null;
}

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interactionPreference, setInteractionPreference] = useState("");
  const [frictionPoint, setFrictionPoint] = useState("");
  const [body, setBody] = useState("");
  const [email, setEmail] = useState("");

  // Reset draft on open so a previous submission doesn't ghost back in.
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setError(null);
    }
  }, [open]);

  // ESC closes the modal — keyboard users shouldn't have to mouse-hunt.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!interactionPreference.trim() && !frictionPoint.trim() && !body.trim()) {
      setError("Tell us at least one thing — any of the boxes is fine.");
      return;
    }
    setBusy(true);
    try {
      await api.submitFeedback({
        pageUrl: typeof window !== "undefined" ? window.location.href : "",
        fromEmail: email.trim() || null,
        body: body.trim() || null,
        interactionPreference: interactionPreference.trim() || null,
        frictionPoint: frictionPoint.trim() || null,
        landingVariant: readVariantCookie(),
      });
      setSubmitted(true);
      setInteractionPreference("");
      setFrictionPoint("");
      setBody("");
      setEmail("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't send. Try again?");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        className="fixed bottom-5 right-5 z-40 rounded-full bg-stone-900 text-white px-5 py-3 shadow-lg hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 text-sm font-medium"
      >
        💬 Feedback
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-title"
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <h2 id="feedback-title" className="text-xl font-semibold tracking-tight">
                Help shape Inkling
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close feedback form"
                className="text-stone-500 hover:text-stone-900 text-xl leading-none -mt-1 -mr-1 px-2"
              >
                ×
              </button>
            </div>

            {submitted ? (
              <div className="mt-6 text-stone-700">
                <p className="font-medium text-stone-900">Thanks — we read every one.</p>
                <p className="mt-2 text-sm">
                  If you left an email, we may reach out with a follow-up question.
                </p>
                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="rounded-lg border border-stone-300 px-4 py-2 text-sm hover:bg-stone-50"
                  >
                    Send another
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-lg bg-stone-900 text-white px-4 py-2 text-sm hover:bg-stone-800"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-4 space-y-4 text-sm">
                <p className="text-stone-600 leading-relaxed">
                  Inkling is in active development and we're listening. Two questions
                  carry the most signal — but say whatever you want.
                </p>

                <label className="block">
                  <span className="block text-stone-800 font-medium mb-1">
                    How would you like to interact with Inkling?
                  </span>
                  <span className="block text-xs text-stone-500 mb-2">
                    Text? Voice? An app? Just photos? Something else? What would feel
                    natural for the way your household already communicates?
                  </span>
                  <textarea
                    rows={3}
                    value={interactionPreference}
                    onChange={(e) => setInteractionPreference(e.target.value)}
                    className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm"
                    placeholder="e.g. mostly iMessage, sometimes voice notes when I'm driving…"
                  />
                </label>

                <label className="block">
                  <span className="block text-stone-800 font-medium mb-1">
                    Where's the friction in your family's setup right now?
                  </span>
                  <span className="block text-xs text-stone-500 mb-2">
                    What breaks down today? Who carries the calendar load? What gets
                    forgotten or double-booked?
                  </span>
                  <textarea
                    rows={3}
                    value={frictionPoint}
                    onChange={(e) => setFrictionPoint(e.target.value)}
                    className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm"
                    placeholder="e.g. my partner and I never see the same school flyer twice…"
                  />
                </label>

                <label className="block">
                  <span className="block text-stone-800 font-medium mb-1">
                    Anything else
                  </span>
                  <textarea
                    rows={2}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm"
                    placeholder="Bug, idea, observation, hot take…"
                  />
                </label>

                <label className="block">
                  <span className="block text-stone-800 font-medium mb-1">
                    Email <span className="font-normal text-stone-500">(optional)</span>
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com — only if you want a reply"
                    className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm"
                  />
                </label>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={busy}
                    className="rounded-lg bg-stone-900 text-white px-5 py-2 font-medium hover:bg-stone-800 disabled:opacity-50"
                  >
                    {busy ? "Sending…" : "Send feedback"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    disabled={busy}
                    className="rounded-lg border border-stone-300 px-5 py-2 font-medium hover:bg-stone-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
// Suppress an unused-export-symbol warning — COOKIE_NAME is referenced
// through the regex pattern but kept exported for parity with abVariant.ts.
void COOKIE_NAME;
